import React from 'react'
import { Button, TextInput } from 'carbon-components-react'
import { SelectASync } from './Select'
import Validator from '../utils/Validator'
import Affiliations from '../validators/Affiliations'
import ApolloClient from '../utils/Apollo'
import { GET_AFFILIATIONS_TYPE_LIST, GET_PPL } from '../graphql/queries'
import { DELETE_AFFILIATIONS } from '../graphql/mutations'
import { compose, graphql } from 'react-apollo'
import WithAlertModal from '../hocs/WithAlertModal'

let locationIpn = window.location.href.slice(window.location.href.search('IPN'))
let IPN = locationIpn.substring(4)
let info = IPN && localStorage.getItem('partyInfo')
  ? JSON.parse(localStorage.getItem('partyInfo'))
  : ''

// VC-164: PPL detection is slug-based (works for any PPL row regardless of
// which environment or `affiliation_type` it was seeded under). Slugs on the
// affiliations table come from Str::slug(name), so both `PPL - UK` rows and
// `PPL India - India` produce slugs beginning with `ppl-`.
const isPplSlug = (slug) => typeof slug === 'string' && slug.startsWith('ppl-')

localStorage.removeItem('partyInfo')
let localUrl = window.location.pathname
let party = '/account/myParty'
let myParty = '/account/myParty'
let windowUrl = localUrl === party ? party : localUrl === myParty ? myParty : '' // eslint-disable-line

const initialState = {
  affiliationId: '',
  affiliationType: '',
  // VC-164: track slug alongside the affiliation ID so the PPL detection is
  // stable across environments (IDs vary between local/staging/prod). Slug
  // is persisted across the PPL OAuth redirect via localStorage below.
  affiliationSlug: info ? info.affiliationSlug || '' : '',
  customName: '',
  selectedAffiliation: '',
  description: '',
  errors: {},
  ipn: IPN,
  affiliationLabel: info ? info.affiliationLabel : ''
}

export class CreateAffiliation extends Validator {
  state = {
    ...initialState,
    affiliationLabel: info && this.props.type === 'PRO' ? '' : initialState.affiliationLabel
  }

  getAffiliationsOptions = (inputValue, type) =>
    new Promise((resolve) => {
      ApolloClient.query({
        query: GET_AFFILIATIONS_TYPE_LIST,
        fetchPolicy: 'network-only'
      }).then(({ data }) => {
        let affiliationsTypes = []
        if (type === 'pro') {
          const pros = data.getAffiliations.filter(
            (item) => item.type === 'pro'
          )
          pros.forEach((affiliation) => {
            if (
              !affiliation.name.toLowerCase().includes(inputValue.toLowerCase())
            ) {
              return
            }
            affiliationsTypes.push({
              value: affiliation.id,
              label: affiliation.name,
              slug: affiliation.slug
            })
          })
        } else {
          // VC-164: previously excluded any name containing "ppl" unless the
          // form was rendered inside My Party. PPL is a Neighboring Rights
          // org and should be selectable everywhere; the OAuth handoff below
          // now gates on inMyParty separately, so we don't need to hide the
          // option to enforce that behaviour.
          const cmos = data.getAffiliations.filter(
            (item) => item.type !== 'pro'
          )

          cmos.forEach((affiliation) => {
            if (
              !affiliation.name.toLowerCase().includes(inputValue.toLowerCase())
            ) {
              return
            }
            affiliationsTypes.push({
              value: affiliation.id,
              label: affiliation.name,
              slug: affiliation.slug
            })
          })
        }
        resolve(
          affiliationsTypes.sort((a, b) => a.label.localeCompare(b.label))
        )
      })
    })

  constructor(props) {
    super(props)
    // Functions
    this.handleChange = this.handleChange.bind(this)
    //
    this.validator = Affiliations
    this.state.selectedAffiliation = props.selectedAffiliation || ''
    this.state.affiliationType = info && this.props.type === 'CMO'
      ? info.affiliationType
      : props.affiliationType || ''
    this.state.url = ''
    this.state.affiliationId = info && this.props.type === 'CMO' ? info.affiliationType : ''
    this.state.description = ''
  }

  submit = () => {
    localStorage.removeItem('partyInfo')
    const variables = {
      affiliationId: this.state.affiliationType,
      number: IPN ? IPN : this.state.affiliationId,
      status: 'verified'
    }

    this.props.onSubmit(variables)

    if (IPN.length) {
      const nextURL = windowUrl
      const nextTitle = ''
      const nextState = { additionalInformation: '' }
      window.history.replaceState(nextState, nextTitle, nextURL)
    }
    this.setState({
      ...initialState,
      ipn: '',
      affiliationType: '',
      affiliationLabel: '',
      affiliationSlug: ''
    })
    initialState.ipn = ''
    initialState.affiliationLabel = ''
    info = ''
    IPN = ''
  }

  handleVerification = () => {
    if (this.state.ipn) {
      this.submit()
      return
    }
    const partyInfo = { ...this.state }
    localStorage.setItem('partyInfo', JSON.stringify(partyInfo))
    // VC-164: only redirect to PPL OAuth when the selection is actually a PPL
    // row (any environment / any type — detected by slug) and we're on the
    // My Party form. Elsewhere the affiliation is entered on someone else's
    // behalf and OAuth doesn't apply.
    if (this.props.inMyParty && isPplSlug(this.state.affiliationSlug) && this.state.url) {
      window.location.href = this.state.url
    }
  }

  componentDidMount() {
    if (IPN.length) {
      const nextURL = windowUrl
      const nextTitle = ''
      const nextState = { additionalInformation: '' }
      window.history.replaceState(nextState, nextTitle, nextURL)
    }
  }

  handleChangeType = (ev) => {
    if (this.props.reserveId) {
      this.props.reserveId(ev.label)
    }
    if (IPN.length) {
      const nextURL = windowUrl
      const nextTitle = ''
      const nextState = { additionalInformation: '' }
      window.history.replaceState(nextState, nextTitle, nextURL)
    }

    if (this.state.ipn) {
      this.setState({
        affiliationType: ev.value,
        affiliationLabel: ev.label,
        affiliationId: '',
        ipn: ''
      })
      localStorage.removeItem('partyInfo')
      initialState.ipn = ''
      initialState.affiliationLabel = ''
      info = ''
      IPN = ''
    } else {
      this.setState({ affiliationType: ev.value, affiliationLabel: ev.label })
    }
    // VC-164: detect PPL by slug rather than the previous hardcoded IDs (7,
    // 19). Slugs are stable across environments (derived from the row name),
    // so this stays correct when new PPL rows are seeded — including the
    // PPL-UK Neighboring Rights row added in the same ticket. OAuth is only
    // meaningful when the account owner is verifying their own PPL account
    // (My Party); on Create/Edit Party the user is entering someone else's
    // affiliation, so we skip the webview handoff there.
    this.setState({ affiliationSlug: ev.slug || '' })
    if (this.props.inMyParty && isPplSlug(ev.slug)) {
      ApolloClient.query({
        query: GET_PPL,
        variables: {
          route: window.location.pathname
        },
        fetchPolicy: 'network-only'
      }).then((url) => this.setState({ url: url.data.getPPL.url }))
    }
  }

  handleChangeId = (ev) => {
    this.setState({
      affiliationId: ev.target.value
    })
  }
  handleChangeInput = (ev) => {
    this.setState({
      customName: ev.target.value
    })
  }
  cancelAddAffiliation = () => {
    this.setState({
      affiliationId: '',
      affiliationType: '',
      affiliationSlug: '',
      selectedAffiliation: '',
      ipn: '',
      affiliationLabel: ''
    })
  }

  render() {
    // VC-164: slug-based PPL detection so this stays correct after new PPL
    // rows are added and across environments where affiliation IDs may differ.
    const pplType = isPplSlug(this.state.affiliationSlug)
    // OAuth flow (auto-filled IPN + PPL-specific buttons) is only offered in
    // the My Party context where the account owner is verifying their own
    // affiliation. Elsewhere the field is user-editable.
    const showPplFlow = pplType && this.props.inMyParty
    return (
      <div className='affiliations-Form'>
        <div className='form'>
          <div>
            <div className='formRow' style={{ width: '100%' }}>
              <SelectASync
                key={this.state.affiliationLabel}
                cacheOptions={false}
                placeholder={
                  this.state.affiliationLabel ? // eslint-disable-line
                    this.state.affiliationLabel :
                    this.state.affiliationType ?
                      this.state.affiliationType :
                      'Select from List...' }
                labelText={this.props.labelText || `Select ${this.props.type}`}
                id='labelId'
                defaultOptions
                loadOptions={(inputValue) =>
                  this.getAffiliationsOptions(inputValue, this.props.types)}
                onChange={this.handleChangeType}
              />
            </div>
            {this.state.affiliationType && (
                <>
                  <div className='formRow'>
                    <TextInput
                      id='affiliationId'
                      name='affiliationId'
                      labelText='Affiliation ID'
                      // VC-164: only disable the field for PPL when we're in
                      // the My Party context (where OAuth will populate it).
                      // On Create/Edit Party the user is entering PPL info
                      // for someone else and needs to be able to type it.
                      disabled={showPplFlow ? true : !!this.state.ipn}
                      onChange={this.handleChangeId}
                      onBlur={this.handleDirty}
                      value={
                        this.state.ipn
                          ? this.state.ipn
                          : this.state.affiliationId
                      }
                    />
                  </div>
                </>
            )}
          </div>
        </div>

        <div className='affiliations-buttons-display affiliation-add'>
          {showPplFlow ? (
            // VC-164: PPL-specific flow with OAuth handoff — only when the
            // account owner is verifying their own PPL affiliation (My Party).
            <>
              <Button
                className=''
                type='button'
                onClick={this.handleVerification}
              >
                {!this.state.affiliationId
                  ? 'Get Affiliation ID'
                  : (this.props.buttonText || `Add ${this.props.type}`)}
              </Button>
              <button
                type='button'
                className='bx--btn bx--btn--secondary bx--btn--mt'
                onClick={this.cancelAddAffiliation}
              >
                Discard
              </button>
            </>
          ) : (
            <Button
              className=''
              type='button'
              onClick={this.submit}
            >
              {this.props.buttonText || `Add ${this.props.type}`}
            </Button>
          )}


        </div>
      </div>
    )
  }
}

export default compose(
  WithAlertModal,
  graphql(DELETE_AFFILIATIONS, { name: 'deleteAffiliations' })
)(CreateAffiliation)
